/**
 * Auto-Capture Detection Hook
 * 
 * Combines focus and stability detection to determine when to auto-capture.
 * Triggers capture when:
 * 1. Focus is sharp enough (not blurry)
 * 2. Camera is stable for required duration
 * 
 * Provides a unified interface for the scanner component and handles
 * the coordination between the two detection systems.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusDetection, FocusDetectionConfig, FocusStatus } from './useFocusDetection';
import { useStabilityDetection, StabilityDetectionConfig } from './useStabilityDetection';

// =============================================================================
// TYPES
// =============================================================================

export type AutoCaptureStatus = 
  | 'waiting'       // Waiting for focus/stability
  | 'focusing'      // Camera moving or out of focus
  | 'stabilizing'   // Focused but still moving
  | 'ready'         // Ready to capture
  | 'cooldown'      // Waiting after capture
  | 'disabled';     // Detection disabled

export interface AutoCaptureResult {
  /** Whether conditions are met for auto-capture */
  shouldCapture: boolean;
  /** Current status of the auto-capture system */
  status: AutoCaptureStatus;
  /** Progress toward capture (0-100) */
  captureProgress: number;
  /** Focus detection result */
  focus: {
    score: number;
    status: FocusStatus;
    isSharpEnough: boolean;
  };
  /** Stability detection result */
  stability: {
    isStable: boolean;
    stableFrameCount: number;
    motionScore: number;
    stableDurationMs: number;
  };
  /** Human-readable guidance for the user */
  guidance: string;
  /** Reset the detection state (e.g., after capture) */
  reset: () => void;
  /** Start cooldown period */
  startCooldown: (durationMs: number) => void;
}

export interface AutoCaptureConfig {
  /** Focus detection configuration */
  focus?: FocusDetectionConfig;
  /** Stability detection configuration */
  stability?: StabilityDetectionConfig;
  /** Minimum stable duration in ms before capture (default: 500) */
  minStableDurationMs?: number;
  /** Whether auto-capture is enabled (default: true) */
  enabled?: boolean;
  /** Callback when capture conditions are first met */
  onReadyToCapture?: () => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

// v2: Reduced from 500ms to 300ms for faster capture response
// Combined with relaxed stability thresholds, this makes auto-capture
// feel much more responsive while still preventing accidental captures
const DEFAULT_MIN_STABLE_DURATION_MS = 300;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Determine current status from focus and stability states
 */
function determineStatus(
  enabled: boolean,
  focusIsSharpEnough: boolean,
  isStable: boolean,
  stableDurationMs: number,
  minStableDurationMs: number,
  inCooldown: boolean
): AutoCaptureStatus {
  if (!enabled) return 'disabled';
  if (inCooldown) return 'cooldown';
  
  if (!focusIsSharpEnough) {
    return 'focusing';
  }
  
  if (!isStable) {
    return 'stabilizing';
  }
  
  if (stableDurationMs >= minStableDurationMs) {
    return 'ready';
  }
  
  return 'stabilizing';
}

/**
 * Calculate capture progress percentage
 */
function calculateProgress(
  focusIsSharpEnough: boolean,
  stableDurationMs: number,
  minStableDurationMs: number
): number {
  if (!focusIsSharpEnough) return 0;
  
  const progress = (stableDurationMs / minStableDurationMs) * 100;
  return Math.min(100, Math.max(0, progress));
}

/**
 * Get guidance text based on current state
 */
function getGuidance(
  status: AutoCaptureStatus,
  focusGuidance: string,
  stabilityGuidance: string
): string {
  switch (status) {
    case 'disabled':
      return 'Auto-capture disabled';
    case 'cooldown':
      return 'Turn the page...';
    case 'focusing':
      return focusGuidance;
    case 'stabilizing':
      return stabilityGuidance;
    case 'ready':
      return 'Capturing...';
    case 'waiting':
    default:
      return 'Point at the book';
  }
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook that combines focus and stability detection for auto-capture
 * 
 * @param videoRef - Reference to the video element
 * @param config - Configuration options
 * @returns Auto-capture detection result
 * 
 * @example
 * ```tsx
 * const videoRef = useRef<HTMLVideoElement>(null);
 * const { shouldCapture, status, captureProgress, guidance, reset, startCooldown } = 
 *   useAutoCaptureDetection(videoRef, {
 *     minStableDurationMs: 500,
 *     onReadyToCapture: () => captureImage(),
 *   });
 * 
 * // Handle capture
 * const captureImage = async () => {
 *   await doCapture();
 *   startCooldown(1500);
 * };
 * ```
 */
export function useAutoCaptureDetection(
  videoRef: React.RefObject<HTMLVideoElement>,
  config: AutoCaptureConfig = {}
): AutoCaptureResult {
  const {
    focus: focusConfig,
    stability: stabilityConfig,
    minStableDurationMs = DEFAULT_MIN_STABLE_DURATION_MS,
    enabled = true,
    onReadyToCapture,
  } = config;
  
  // Internal state
  const [inCooldown, setInCooldown] = useState(false);
  const cooldownTimeoutRef = useRef<number | null>(null);
  const hasTriggeredRef = useRef(false);
  
  // Use focus detection
  const focusResult = useFocusDetection(videoRef, {
    ...focusConfig,
    enabled: enabled && !inCooldown,
  });
  
  // Use stability detection
  const stabilityResult = useStabilityDetection(videoRef, {
    ...stabilityConfig,
    enabled: enabled && !inCooldown,
  });
  
  // Determine current status
  const status = determineStatus(
    enabled,
    focusResult.isSharpEnough,
    stabilityResult.isStable,
    stabilityResult.stableDurationMs,
    minStableDurationMs,
    inCooldown
  );
  
  // Calculate capture progress
  const captureProgress = calculateProgress(
    focusResult.isSharpEnough,
    stabilityResult.stableDurationMs,
    minStableDurationMs
  );
  
  // Determine if should capture
  const shouldCapture = status === 'ready' && !hasTriggeredRef.current;
  
  // Get guidance text
  const guidance = getGuidance(
    status,
    focusResult.guidance,
    stabilityResult.guidance
  );
  
  // Reset function
  const reset = useCallback(() => {
    hasTriggeredRef.current = false;
    setInCooldown(false);
    if (cooldownTimeoutRef.current !== null) {
      window.clearTimeout(cooldownTimeoutRef.current);
      cooldownTimeoutRef.current = null;
    }
  }, []);
  
  // Start cooldown function
  const startCooldown = useCallback((durationMs: number) => {
    hasTriggeredRef.current = true;
    setInCooldown(true);
    
    if (cooldownTimeoutRef.current !== null) {
      window.clearTimeout(cooldownTimeoutRef.current);
    }
    
    cooldownTimeoutRef.current = window.setTimeout(() => {
      setInCooldown(false);
      hasTriggeredRef.current = false;
      cooldownTimeoutRef.current = null;
    }, durationMs);
  }, []);
  
  // Trigger callback when ready to capture
  useEffect(() => {
    if (shouldCapture && onReadyToCapture && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      onReadyToCapture();
    }
  }, [shouldCapture, onReadyToCapture]);
  
  // Reset triggered state when stability is lost
  useEffect(() => {
    if (!stabilityResult.isStable && !inCooldown) {
      hasTriggeredRef.current = false;
    }
  }, [stabilityResult.isStable, inCooldown]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimeoutRef.current !== null) {
        window.clearTimeout(cooldownTimeoutRef.current);
      }
    };
  }, []);
  
  return {
    shouldCapture,
    status,
    captureProgress,
    focus: {
      score: focusResult.focusScore,
      status: focusResult.focusStatus,
      isSharpEnough: focusResult.isSharpEnough,
    },
    stability: {
      isStable: stabilityResult.isStable,
      stableFrameCount: stabilityResult.stableFrameCount,
      motionScore: stabilityResult.motionScore,
      stableDurationMs: stabilityResult.stableDurationMs,
    },
    guidance,
    reset,
    startCooldown,
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

// AutoCaptureConfig already exported at definition (line 57)
